import { OiReport } from "../OiReport";

interface OiPrintReportProps {
    report: OiReport;
}

export function generatePrintHTML(report: OiReport): string {
    const formatDateTime = (dateTimeString: string) => {
        if (!dateTimeString) return "Not set";
        if (dateTimeString.includes(' ')) {
            dateTimeString = dateTimeString.replace(' ', 'T');
        }
        if (!dateTimeString.endsWith('Z')) {
            dateTimeString += 'Z';
        }
        try {
            const date = new Date(dateTimeString);
            return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        } catch {
            console.error('Invalid date string:', dateTimeString);
            return dateTimeString;
        }
    };

    // Generate the current date/time
    const now = new Date();
    const currentDate = now.toLocaleDateString();
    const currentTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return `
        <!DOCTYPE html>
        <html>
            <head>
                <title>Print Report - ${report.testName || 'Unknown Test'}</title>
                <script src="https://cdn.tailwindcss.com"></script>
                <script>
                    tailwind.config = {
                        theme: {
                            extend: {
                                colors: {
                                    'blue-gradient': 'linear-gradient(90deg, #3b82f6, #1e40af, #1d4ed8)',
                                },
                                fontFamily: {
                                    'system': ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'sans-serif']
                                }
                            }
                        }
                    }
                </script>
                <style>
                    .blue-top-gradient {
                        background: linear-gradient(90deg, #3b82f6, #1e40af, #1d4ed8);
                    }
                    .header-gradient {
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    }
                    .comment-gradient {
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    }
                    .footer-gradient {
                        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                    }
                    .field-label::before {
                        content: '';
                        width: 3px;
                        height: 16px;
                        background: #3b82f6;
                        border-radius: 2px;
                        display: inline-block;
                    }
                    .meta-icon::before {
                        content: '🕒';
                        font-size: 14px;
                        margin-right: 8px;
                    }
                    .comment-quote::before {
                        content: '"';
                        position: absolute;
                        top: 8px;
                        left: 12px;
                        font-size: 24px;
                        color: #cbd5e1;
                        font-weight: bold;
                    }
                    .attachment-icon::before {
                        content: '📎';
                        font-size: 14px;
                        margin-right: 8px;
                    }
                    .status-check::before {
                        content: '✓';
                        font-weight: bold;
                        margin-right: 6px;
                    }
                    .footer-line::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 50%;
                        transform: translateX(-50%);
                        width: 60px;
                        height: 2px;
                        background: #cbd5e1;
                        border-radius: 1px;
                    }
                    @media print {
                        body { 
                            padding: 5px !important; 
                            font-size: 11px !important; 
                        }
                        .print-hide { display: none !important; }
                        .print-transparent { background: transparent !important; }
                        .print-no-shadow { box-shadow: none !important; }
                        .print-compact { margin-bottom: 8px !important; }
                        .print-small-text { font-size: 10px !important; }
                        .print-header { padding: 12px !important; margin: -5px -5px 12px -5px !important; }
                        .print-section { padding: 8px !important; margin-bottom: 8px !important; }
                        .print-grid { gap: 16px !important; padding: 8px !important; }
                        .print-field { margin-bottom: 6px !important; }
                        .print-logo { width: 60px !important; height: 60px !important; }
                        @page { 
                            size: A4; 
                            margin: 0.8cm; 
                        }
                    }
                </style>
            </head>
            <body class="font-system leading-tight text-gray-800 bg-white p-4 text-xs antialiased">
                <div class="print-report max-w-4xl mx-auto bg-white print:shadow-none">
                    <!-- Blue gradient top border -->
                    <div class="blue-top-gradient h-1 w-full absolute top-0 left-0 right-0 print-hide"></div>
                    
                    <div class="report-content p-4">
                        <!-- Header -->
                        <div class="header header-gradient p-4 -m-4 relative flex items-center gap-4 print-header print:bg-transparent">
                            <img src="https://media.licdn.com/dms/image/v2/D4D0BAQGsbCuyVXmBLQ/company-logo_200_200/company-logo_200_200/0/1695278839135/seco_tools_logo?e=2147483647&v=beta&t=YJudHM6u6wbe03oViaO1WuVbvhB4Ey6PknuwnDnrpRk" alt="Company Logo" class="logo w-16 h-16 object-contain rounded bg-white p-1 print-logo print:shadow-none" onerror="this.style.display='none'" />
                            <div class="header-content flex-1">
                                <h1 class="text-xl font-bold mb-1 text-gray-900 tracking-tight">Test Result Report</h1>
                                <h2 class="text-lg font-semibold mb-2 text-blue-700 tracking-wide">${report.testName || 'Unknown Test'}</h2>
                            </div>
                        </div>

                        <!-- Two-column layout for basic info -->
                        <div class="section bg-white p-4 print-section print:bg-transparent">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded print-grid print:bg-transparent">
                                <div>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test Instrument: ${report.testSelection}</div>

                                    </div>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test ID: ${report.testName}</div>
                                    </div>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test Facility: ${report.elementName ? report.elementName : ''}</div>
                                    </div>
                                    <div class="field relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Author: ${report.author}</div>
                                    </div>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test Start Date: ${report.startTime ? formatDateTime(report.startTime) : ''}</div>
                                    </div>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test End Date: ${report.endTime ? formatDateTime(report.endTime) : ''}</div>
                                    </div>
                                    <br/>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Test comment</div>
                                    </div>
                                    <div class="comment-gradient rounded mt-1 relative min-h-[40px]  text-slate-600 text-xs print:bg-transparent">
                                        <div class="comment-content pl-4 leading-tight">
                                            ${report.comment || ''}
                                        </div>
                                    </div>
                                    <br/>
                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Manufacturing machine:${report.taggedElements.length > 0 ? report.taggedElements.join(', ') : '-'}</div>
                                    </div>

                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Manufacturing comment</div>
                                    </div>

                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Tagged Elements Start Time: ${report.taggedElementsStart ? formatDateTime(report.taggedElementsStart[0]) : ''}</div>
                                    </div>

                                    <div class="field mb-2 relative print-field">
                                        <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Tagged Elements End Time: ${report.taggedElementsEnd ? formatDateTime(report.taggedElementsEnd[0]) : ''}</div>
                                    </div>

                                    <div class="comment-gradient rounded mt-1 relative min-h-[40px]  text-slate-600 text-xs print:bg-transparent">
                                        <div class="comment-content pl-4 leading-tight">
                                            ${report.taggedElementsComments.length > 0 ? report.taggedElementsComments.join(', ') : '-'}
                                        </div>
                                    </div>



                            <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Tags:</div>
                                <div class="tag-list flex flex-wrap gap-1 mt-1">
                                    ${report.tags.length > 0
            ? report.tags.map(tag => `<span class="tag-item bg-sky-50 text-sky-900 py-1 px-2 rounded-full text-xs font-medium print:bg-transparent">${tag}</span>`).join('')
            : ''
        }
                                </div>
                            <div class="field mb-2 relative print-field">
                                <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">DMC:</div>
                                <div class="field-value text-gray-600 text-xs leading-tight py-1 px-2 bg-gray-50 rounded font-medium print:bg-transparent">${report.dataMatrixCodes.length > 0 ? report.dataMatrixCodes.join(', ') : 'No DMC'}</div>
                            </div>
                            <div class="field mb-2 relative print-field">
                                <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Tagged Staff:</div>
                                <div class="field-value text-gray-600 text-xs leading-tight py-1 px-2 bg-gray-50 rounded font-medium print:bg-transparent">${report.taggedUsers.length > 0 ? report.taggedUsers.join(', ') : 'No staff tagged'}</div>
                            </div>

                                                        <div class="field mb-2 relative print-field">
                                <div class="field-label font-semibold text-gray-700 text-xs mb-1 flex items-center">Attachments:</div>
                                <div class="field-value text-gray-600 text-xs leading-tight py-1 px-2 bg-gray-50 rounded font-medium print:bg-transparent">${report.referencedDocuments.length > 0 ? report.referencedDocuments.map(f => f.fileName).join(', ') : 'No attachments'}</div>
                            </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </body>
        </html>
    `;
}

